import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { auditApi, type PaginatedAuditResponse } from '@/api/admin/audit';

interface AuditModalProfile {
  id: string;
  name: string;
}

interface UseProfileAuditModalReturn {
  isOpen: boolean;
  profile: AuditModalProfile | null;
  currentPage: number;
  auditData: PaginatedAuditResponse | undefined;
  isLoading: boolean;
  openModal: (profile: AuditModalProfile) => void;
  closeModal: () => void;
  handlePageChange: (page: number) => void;
}

export function useProfileAuditModal(): UseProfileAuditModalReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [profile, setProfile] = useState<AuditModalProfile | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const { data: auditData, isLoading } = useQuery({
    queryKey: ['profile-audit', profile?.id, currentPage],
    queryFn: () => auditApi.findByEntity(profile?.id!, { page: currentPage, limit: 10 }),
    enabled: !!profile?.id && isOpen,
  });

  const openModal = useCallback((selectedProfile: AuditModalProfile) => {
    setProfile(selectedProfile);
    setCurrentPage(1);
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setProfile(null);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  return {
    isOpen,
    profile,
    currentPage,
    auditData,
    isLoading,
    openModal,
    closeModal,
    handlePageChange,
  };
}
